import { Check, X } from "lucide-react";

interface PasswordRequirement {
  label: string;
  met: boolean;
}

function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { label: "Mindestens 8 Zeichen", met: password.length >= 8 },
    { label: "Enthält Großbuchstaben", met: /[A-Z]/.test(password) },
    { label: "Enthält Kleinbuchstaben", met: /[a-z]/.test(password) },
    { label: "Enthält eine Zahl", met: /[0-9]/.test(password) },
  ];
}

function getStrengthLevel(requirements: PasswordRequirement[]): {
  level: number;
  label: string;
  color: string;
} {
  const metCount = requirements.filter((r) => r.met).length;
  if (metCount === 0) return { level: 0, label: "", color: "" };
  if (metCount === 1) return { level: 1, label: "Schwach", color: "bg-red-500" };
  if (metCount === 2) return { level: 2, label: "Mäßig", color: "bg-orange-500" };
  if (metCount === 3) return { level: 3, label: "Gut", color: "bg-yellow-500" };
  return { level: 4, label: "Stark", color: "bg-green-500" };
}

export function PasswordStrengthMeter({ password }: { password: string }) {
  const requirements = getPasswordRequirements(password);
  const strength = getStrengthLevel(requirements);

  if (!password) return null;

  return (
    <div className="space-y-2 mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              level <= strength.level ? strength.color : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      {strength.label && (
        <p className={`text-xs font-medium ${
          strength.level <= 1 ? "text-red-600" :
          strength.level === 2 ? "text-orange-600" :
          strength.level === 3 ? "text-yellow-600" :
          "text-green-600"
        }`}>
          Passwortstärke: {strength.label}
        </p>
      )}
      <ul className="space-y-1">
        {requirements.map((req) => (
          <li key={req.label} className="flex items-center gap-1.5 text-xs">
            {req.met ? (
              <Check className="h-3 w-3 text-green-600 shrink-0" />
            ) : (
              <X className="h-3 w-3 text-gray-400 shrink-0" />
            )}
            <span className={req.met ? "text-green-700" : "text-gray-500"}>
              {req.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
